import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { 
  Upload, FileText, Image, File, Download, Trash2, Search,
  Scan, CheckCircle, Eye, X, Plus
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FilesPage = () => {
  const { user } = useAuth();
  const t = useTranslation(user?.language || 'ja');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchFiles();
  }, [search]);

  const fetchFiles = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      
      const { data } = await axios.get(`${API_URL}/api/files?${params}`, {
        withCredentials: true,
      });
      setFiles(data.files);
    } catch (e) {
      console.error('Failed to fetch files:', e);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    
    if (fileList.length === 1) {
      formData.append('file', fileList[0]);
      
      try {
        const { data } = await axios.post(`${API_URL}/api/files/upload`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            setUploadProgress(Math.round((e.loaded * 100) / e.total));
          },
        });
        
        toast.success(
          data.is_business_card
            ? (user?.language === 'ja' ? '名刺を検出しました' : 'Business card detected')
            : t.common.success
        );
        
        if (data.created_contact) {
          toast.info(
            user?.language === 'ja'
              ? `連絡先「${data.created_contact.name}」を作成`
              : `Contact "${data.created_contact.name}" created`
          );
        }
        
        fetchFiles();
      } catch (e) {
        toast.error(t.common.error);
      }
    } else {
      for (const file of fileList) {
        formData.append('files', file);
      }
      
      try {
        await axios.post(`${API_URL}/api/files/upload/batch`, formData, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            setUploadProgress(Math.round((e.loaded * 100) / e.total));
          },
        });
        
        toast.success(
          user?.language === 'ja' 
            ? `${fileList.length}件アップロード完了` 
            : `Uploaded ${fileList.length} files`
        );
        fetchFiles();
      } catch (e) {
        toast.error(t.common.error);
      }
    }
    
    setUploading(false);
    setUploadProgress(0);
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleUpload(e.dataTransfer.files);
    }
  }, []);

  const handleDownload = async (file) => {
    try {
      const response = await axios.get(`${API_URL}/api/files/${file.id}/download`, {
        withCredentials: true,
        responseType: 'blob',
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(t.common.error);
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm(t.common.confirm)) return;
    try {
      await axios.delete(`${API_URL}/api/files/${fileId}`, {
        withCredentials: true,
      });
      toast.success(t.common.success);
      fetchFiles();
    } catch (e) {
      toast.error(t.common.error);
    }
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return <Image className="w-5 h-5 text-green-400" />;
    if (mimeType?.includes('pdf')) return <FileText className="w-5 h-5 text-destructive" />;
    return <File className="w-5 h-5 text-primary" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return new Intl.DateTimeFormat(user?.language === 'en' ? 'en-US' : 'ja-JP', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const openPreview = (file) => {
    setSelectedFile(file);
    setPreviewOpen(true);
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="files-page">
      {/* Header - Mobile responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-glow">{t.files.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {user?.language === 'ja' ? 'OCR自動整理・名刺自動登録' : 'Auto OCR & business card detection'}
          </p>
        </div>
        
        {/* Upload Button */}
        <div className="flex gap-2 w-full sm:w-auto">
          <Button
            onClick={() => fileInputRef.current?.click()}
            className="flex-1 sm:flex-none btn-primary"
            disabled={uploading}
            data-testid="upload-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            {user?.language === 'ja' ? 'アップロード' : 'Upload'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
            className="hidden"
            onChange={(e) => handleUpload(e.target.files)}
          />
        </div>
      </div>

      {/* Simple Drop Zone - Hidden on mobile, shown on desktop */}
      <div
        className={`hidden sm:block border-2 border-dashed rounded-sm p-6 text-center transition-all ${
          dragActive ? 'border-primary bg-primary/5 border-glow' : 'border-border hover:border-primary/50'
        } ${uploading ? 'scanning-animation' : ''}`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        data-testid="file-dropzone"
      >
        {uploading ? (
          <div className="space-y-3">
            <Scan className="w-8 h-8 mx-auto text-primary animate-pulse" />
            <p className="text-primary font-medium text-sm">{user?.language === 'ja' ? '処理中...' : 'Processing...'}</p>
            <Progress value={uploadProgress} className="w-48 mx-auto" />
          </div>
        ) : (
          <>
            <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="font-medium text-sm">{user?.language === 'ja' ? 'ここにファイルをドロップ' : 'Drop files here'}</p>
          </>
        )}
      </div>

      {/* Mobile upload progress */}
      {uploading && (
        <div className="sm:hidden p-4 bg-secondary rounded-sm">
          <div className="flex items-center gap-3">
            <Scan className="w-6 h-6 text-primary animate-pulse" />
            <div className="flex-1">
              <p className="text-sm font-medium">{user?.language === 'ja' ? '処理中...' : 'Processing...'}</p>
              <Progress value={uploadProgress} className="mt-2" />
            </div>
          </div>
        </div>
      )}

      {/* Search - Full width on mobile */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t.search.placeholder}
          className="pl-9 input-cyber w-full"
          data-testid="file-search-input"
        />
      </div>

      {/* Files List */}
      <Card className="card-cyber">
        <CardHeader className="pb-2 px-4 sm:px-6">
          <CardTitle className="text-base sm:text-lg flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            {user?.language === 'ja' ? 'ファイル一覧' : 'Files'} 
            <Badge variant="outline" className="font-mono ml-2">{files.length}</Badge>
          </CardTitle>
        </CardHeader>
        
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">{t.common.loading}</div>
          ) : files.length === 0 ? (
            <div className="p-8 sm:p-12 text-center">
              <FileText className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground text-sm">{t.files.noFiles}</p>
            </div>
          ) : (
            <div className="divide-y divide-border max-h-[60vh] overflow-y-auto">
              {files.map((file) => (
                <div 
                  key={file.id} 
                  className="flex items-center gap-3 p-3 sm:p-4 hover:bg-secondary/50 transition-colors cursor-pointer"
                  onClick={() => file.mime_type?.startsWith('image/') ? openPreview(file) : null}
                  data-testid={`file-row-${file.id}`}
                >
                  {/* Thumbnail for images */}
                  <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-sm overflow-hidden bg-secondary/50 flex items-center justify-center">
                    {file.mime_type?.startsWith('image/') ? (
                      <img
                        src={`${API_URL}/api/files/${file.id}/download`}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      getFileIcon(file.mime_type)
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{file.title}</p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="font-mono">{formatFileSize(file.file_size)}</span>
                      <span className="hidden sm:inline">{formatDate(file.created_at)}</span>
                      {file.ocr_text && <CheckCircle className="w-3 h-3 text-green-400" />}
                      {file.is_business_card && (
                        <Badge className="sm:hidden bg-green-500/20 text-green-400 border-green-500/30 text-[10px] px-1 py-0">
                          {user?.language === 'ja' ? '名刺' : 'Card'}
                        </Badge>
                      )}
                    </div>
                  </div>
                  {file.is_business_card && (
                    <Badge className="hidden sm:flex bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                      {user?.language === 'ja' ? '名刺' : 'Card'}
                    </Badge>
                  )}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {file.mime_type?.startsWith('image/') && (
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); openPreview(file); }} data-testid={`file-preview-${file.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={(e) => { e.stopPropagation(); handleDownload(file); }} data-testid={`file-download-${file.id}`}>
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      onClick={(e) => { e.stopPropagation(); handleDelete(file.id); }}
                      data-testid={`file-delete-${file.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* File Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-3xl max-h-[90vh] card-cyber border-glow p-0 overflow-hidden">
          <DialogHeader className="p-3 sm:p-4 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between gap-2">
              <DialogTitle className="text-sm sm:text-base truncate">{selectedFile?.title}</DialogTitle>
              <div className="flex items-center gap-1 flex-shrink-0">
                {selectedFile && (
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => handleDownload(selectedFile)}>
                    <Download className="w-4 h-4" />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setPreviewOpen(false)} className="h-8 w-8 p-0">
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
            {selectedFile && (
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                <span className="font-mono">{formatFileSize(selectedFile.file_size)}</span>
                {selectedFile.is_business_card && (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 text-xs">
                    {user?.language === 'ja' ? '名刺' : 'Card'}
                  </Badge>
                )}
                {selectedFile.ocr_text && (
                  <Badge variant="outline" className="text-xs">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    OCR
                  </Badge>
                )}
              </div>
            )}
          </DialogHeader>
          <div className="overflow-y-auto" style={{ maxHeight: 'calc(90vh - 80px)' }}>
            {selectedFile && (
              <div className="space-y-0">
                {/* Image - fits within viewport */}
                <div className="flex items-center justify-center bg-black/30 p-2 sm:p-4">
                  <img
                    src={`${API_URL}/api/files/${selectedFile.id}/download`}
                    alt={selectedFile.title}
                    className="max-w-full max-h-[50vh] sm:max-h-[55vh] object-contain rounded-sm"
                    style={{ width: 'auto', height: 'auto' }}
                    data-testid="preview-image"
                  />
                </div>
                {/* OCR Result */}
                {selectedFile.ocr_text && (
                  <div className="p-3 sm:p-4 border-t border-border">
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Scan className="w-4 h-4 text-primary" />
                      {user?.language === 'ja' ? 'OCR結果' : 'OCR Result'}
                    </h4>
                    <pre className="text-xs sm:text-sm whitespace-pre-wrap font-mono text-muted-foreground bg-secondary/50 p-3 rounded-sm max-h-[30vh] overflow-y-auto">
                      {selectedFile.ocr_text}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FilesPage;
